const NOT_SET = "Not set";

const FIELD_LABELS = {
  area: "Area",
  equipment_tag: "Equipment tag",
  description: "Description",
  zone: "Zone",
  system_component: "System / component",
  manufacturer: "Manufacturer",
  model_no: "Model number",
  serial_no: "Serial number",
  protection_class: "Protection class",
  gas_group: "Gas group",
  temperature_class: "Temperature class",
  ip_rating: "IP rating",
  certifying_authority: "Certifying authority",
  certificate_number: "Certificate number",
  category_epl: "Category / EPL",
  work_order: "Work order",
  inspection_date: "Inspection date",
  inspection_type: "Inspection type",
  inspection_result: "Inspection result",
  inspector: "Inspector",
  failure_notes: "Item notes",
  image_uuid: "Photo",
  checklist_answers: "Checklist answers",
  checklist_summary: "Checklist summary",
  checklist_questions_snapshot: "Checklist questions",
  checklist_inspected_at: "Checklist recorded at",
  checklist_inspector: "Checklist inspector",
  last_useredit: "Last edited by",
  edit_date: "Edit date",
  is_deleted: "Item status",
  deleted_by: "Deleted by",
  deleted_at: "Deleted at",
  id: "Record ID",
  equipment_id: "Equipment ID",
  version: "Record version",
  client_version: "App version",
  created_at: "Created at",
  updated_at: "Updated at",
  performed_at: "Recorded at",
  performed_by: "Recorded by",
  source: "Source",
  recorded_changes: "Recorded changes",
};

const normalizeKey = (key) => String(key ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
const FIELD_ALIASES = Object.fromEntries(
  Object.entries(FIELD_LABELS).flatMap(([key, label]) => [
    [normalizeKey(key), key],
    [normalizeKey(label), key],
  ])
);
Object.assign(FIELD_ALIASES, {
  tag: "equipment_tag",
  system: "system_component",
  categepl: "category_epl",
  protectclass: "protection_class",
  tempclass: "temperature_class",
  ipclass: "ip_rating",
  certauth: "certifying_authority",
  certno: "certificate_number",
  type: "inspection_type",
  result: "inspection_result",
  inspdate: "inspection_date",
  photouuid: "image_uuid",
  checklist: "checklist_summary",
  checklistdate: "checklist_inspected_at",
  deleted: "is_deleted",
});

const CALENDAR_FIELDS = new Set(["inspection_date", "edit_date"]);
const DETAIL_FIELDS = new Set([
  "edit_date", "last_useredit", "checklist_inspector", "checklist_inspected_at",
  "checklist_questions_snapshot", "id", "equipment_id", "version", "client_version",
  "created_at", "updated_at", "performed_at", "performed_by", "source", "deleted_by", "deleted_at",
]);

export function canonicalField(key) {
  const normalized = normalizeKey(key);
  if (Object.prototype.hasOwnProperty.call(FIELD_ALIASES, normalized)) return FIELD_ALIASES[normalized];
  return String(key ?? "")
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
}

export function getFieldLabel(key) {
  const field = canonicalField(key);
  if (Object.prototype.hasOwnProperty.call(FIELD_LABELS, field)) return FIELD_LABELS[field];
  const words = field.replace(/_/g, " ");
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : "Value";
}

const isEmpty = (value) => value === null || value === undefined ||
  (typeof value === "string" && value.trim() === "");

// JSONB normally arrives decoded, but older exported records can contain JSON strings.
function parseStructuredValue(value) {
  if (typeof value !== "string") return value;
  const firstCharacter = value.trimStart().charAt(0);
  if (firstCharacter !== "[" && firstCharacter !== "{") return value;
  try {
    return JSON.parse(value);
  } catch (_) {
    return value;
  }
}

function calendarDay(value) {
  const text = value instanceof Date && !Number.isNaN(value.getTime())
    ? value.toISOString()
    : String(value ?? "");
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:$|T|\s)/.exec(text);
  if (!match) return null;
  const [, year, month, day] = match;
  const date = new Date(`${year}-${month}-${day}T12:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === `${year}-${month}-${day}`
    ? `${year}-${month}-${day}` : null;
}

export function formatHistoryDate(value, { dateOnly = false } = {}) {
  if (isEmpty(value)) return NOT_SET;
  if (dateOnly) {
    const day = calendarDay(value);
    if (!day) return String(value);
    // Keep database DATE values on their recorded day in every browser timezone.
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric", month: "short", day: "numeric", timeZone: "UTC",
    }).format(new Date(`${day}T12:00:00Z`));
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  }).format(date);
}

function formatValue(value, field, ancestors) {
  if (isEmpty(value)) return NOT_SET;
  const parsed = parseStructuredValue(value);
  if (parsed !== value) return formatValue(parsed, field, ancestors);
  if (value instanceof Date) return formatHistoryDate(value, { dateOnly: CALENDAR_FIELDS.has(field) });
  if (typeof value === "object") {
    if (ancestors.has(value)) return "Repeated reference";
    const nextAncestors = new Set(ancestors).add(value);
    if (Array.isArray(value)) {
      return value.length ? value.map((entry) => formatValue(entry, "", nextAncestors)).join("\n") : NOT_SET;
    }
    const entries = Object.entries(value);
    return entries.length ? entries.map(([key, entry]) =>
      `${getFieldLabel(key)}: ${formatValue(entry, canonicalField(key), nextAncestors)}`
    ).join("\n") : NOT_SET;
  }
  if (field === "is_deleted") {
    if (value === true || value === "true") return "Deleted";
    if (value === false || value === "false") return "Active";
  }
  if (CALENDAR_FIELDS.has(field)) return formatHistoryDate(value, { dateOnly: true });
  if (field.endsWith("_at")) return formatHistoryDate(value);
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if ((field === "inspection_result" || field === "checklist_answer") &&
      typeof value === "string" && /^(na|n\/a)$/i.test(value.trim())) return "Not applicable";
  return String(value);
}

export function formatHistoryValue(value, field = "") {
  return formatValue(value, canonicalField(field), new Set());
}

function normalizeAnswer(value) {
  if (isEmpty(value)) return null;
  if (typeof value !== "string") return undefined;
  const answer = value.trim();
  if (/^pass$/i.test(answer)) return "Pass";
  if (/^fail$/i.test(answer)) return "Fail";
  if (/^(na|n\/a)$/i.test(answer)) return "NA";
  return answer;
}

function checklistEntries(raw) {
  const value = parseStructuredValue(raw);
  if (isEmpty(value)) return [];
  if (!Array.isArray(value)) return null;
  const occurrences = new Map();
  const entries = [];
  for (const entry of value) {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) return null;
    if (Object.keys(entry).some((key) => !["question", "q", "answer", "value", "result"].includes(key))) return null;
    const rawQuestion = entry.question ?? entry.q;
    if (typeof rawQuestion !== "string" || !rawQuestion.trim()) return null;
    const question = rawQuestion.trim().replace(/\s+/g, " ");
    const answer = normalizeAnswer(entry.answer ?? entry.value ?? entry.result ?? null);
    if (answer === undefined) return null;
    const occurrence = (occurrences.get(question) || 0) + 1;
    occurrences.set(question, occurrence);
    entries.push({ key: JSON.stringify([question, occurrence]), question, answer });
  }
  return entries;
}

function countAnswers(entries) {
  return entries.reduce((counts, entry) => {
    if (entry.answer === "Pass") counts.pass += 1;
    if (entry.answer === "Fail") counts.fail += 1;
    if (entry.answer === "NA") counts.na += 1;
    return counts;
  }, { pass: 0, fail: 0, na: 0, total: entries.length });
}

function buildChecklist(from, to) {
  const before = checklistEntries(from);
  const after = checklistEntries(to);
  if (!before || !after) return null;
  const beforeMap = new Map(before.map((entry) => [entry.key, entry]));
  const afterMap = new Map(after.map((entry) => [entry.key, entry]));
  const ordered = [...after, ...before.filter((entry) => !afterMap.has(entry.key))];
  const rows = ordered.map((entry) => {
    const previous = beforeMap.get(entry.key);
    const current = afterMap.get(entry.key);
    const kind = !previous ? "added" : !current ? "removed"
      : previous.answer !== current.answer ? "changed" : "unchanged";
    return {
      key: entry.key, question: entry.question,
      from: previous?.answer ?? null, to: current?.answer ?? null,
      changed: kind !== "unchanged", kind,
    };
  });
  return {
    rows, changedCount: rows.filter((row) => row.changed).length, totalCount: rows.length,
    beforeCounts: countAnswers(before), afterCounts: countAnswers(after), raw: { from, to },
  };
}

export function getHistoryChanges(row) {
  const changes = { fields: [], details: [], checklist: null };
  const raw = parseStructuredValue(row?.changed_fields);
  if (isEmpty(raw)) return changes;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    changes.fields.push({
      key: "recorded_changes", field: "recorded_changes", label: "Recorded changes", from: null, to: raw,
    });
    return changes;
  }
  for (const [key, rawDifference] of Object.entries(raw)) {
    const field = canonicalField(key);
    const difference = parseStructuredValue(rawDifference);
    const isDifference = difference && typeof difference === "object" && !Array.isArray(difference) &&
      (Object.prototype.hasOwnProperty.call(difference, "from") || Object.prototype.hasOwnProperty.call(difference, "to"));
    const from = isDifference ? difference.from : null;
    const to = isDifference ? difference.to : rawDifference;
    if (field === "checklist_answers" && !changes.checklist) {
      const checklist = buildChecklist(from, to);
      if (checklist) {
        changes.checklist = checklist;
        continue;
      }
    }
    const item = { key, field, label: getFieldLabel(field), from, to };
    if (CALENDAR_FIELDS.has(field) && !isEmpty(from) && !isEmpty(to) &&
        from !== to && calendarDay(from) && calendarDay(from) === calendarDay(to)) {
      item.note = "The calendar date is unchanged; the recorded time or format changed.";
    }
    const isDetail = DETAIL_FIELDS.has(field) ||
      (field !== "image_uuid" && /(?:^|_)(?:id|uuid|version)$/.test(field));
    changes[isDetail ? "details" : "fields"].push(item);
  }
  return changes;
}

export function getEventPresentation(row) {
  const event = String(row?.event_type || "").toLowerCase();
  if (event === "created") return { label: "Item created", color: "success" };
  if (event === "deleted") return { label: "Item deleted", color: "error" };
  if (event === "restored") return { label: "Item restored", color: "success" };
  if (event === "inspected") return { label: "Inspection updated", color: "info" };
  if (event === "edited") {
    const changes = getHistoryChanges(row);
    const onlyDetails = changes.details.length > 0 && changes.fields.length === 0 && !changes.checklist;
    return { label: onlyDetails ? "Record details updated" : "Equipment edited", color: "primary" };
  }
  return { label: event ? getFieldLabel(event) : "History recorded", color: "default" };
}

const counted = (count, singular, plural = `${singular}s`) => `${count} ${count === 1 ? singular : plural}`;

export function getHistorySummary(row, changes = getHistoryChanges(row)) {
  const phrases = [];
  if (changes.fields.length) {
    phrases.push(changes.fields.length <= 2
      ? `${changes.fields.map((field) => field.label).join(" and ")} updated`
      : `${counted(changes.fields.length, "field")} changed`);
  }
  if (changes.checklist?.changedCount) {
    phrases.push(`${counted(changes.checklist.changedCount, "checklist answer")} changed`);
  } else if (changes.checklist) {
    phrases.push("Checklist answers unchanged");
  }
  if (phrases.length === 0 && changes.details.length) {
    phrases.push(changes.details.length <= 2
      ? `${changes.details.map((field) => field.label).join(" and ")} updated`
      : `${counted(changes.details.length, "record detail")} updated`);
  }
  return phrases.join(" · ") || "No field changes recorded";
}
